import React, { FC } from 'react';
import { useTheme } from '@mui/material/styles';
import { Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import { Box, InputAdornment, Tab, Tabs, Typography, Unstable_Grid2 as Grid } from '@mui/material';

import { ContentCommentary, Network } from 'api/public';
import { SocialAccount } from 'api';
import { MentionsEditor, PaddedPaper, SocialIcon, TextCounter } from 'components';
import { getSocialAccountName } from 'helpers';

import { PublicShareButton } from 'features/content/partials';
import { PublicContentCommentary, Variations } from 'features/content/types';
import { publicSharingSchema } from 'features/content/validations';
import theme, { pxToRem } from 'theme';

type Props = {
  network: Network;
  socialAccount?: SocialAccount;
  variations: Variations;
  shouldShowMentionsEditor: boolean;
  onSubmit: (shareCopy: PublicContentCommentary) => void;
  loading: boolean;
  hasShared: boolean;
  lockCommentary: boolean;
  limit: number;
};

const styles = {
  tab: {
    fontSize: `${pxToRem(14)} !important`,
    minWidth: 3,
    padding: `${theme.spacing(0, 2.5, 0, 0)} !important`,
    minHeight: 2,
  },
};
const PublicShareForm: FC<Props> = ({
  network,
  socialAccount,
  variations,
  shouldShowMentionsEditor,
  onSubmit,
  loading,
  hasShared,
  lockCommentary,
  limit,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();

  function handleSubmit(values: Variations) {
    let message = values.messages[values.index];
    if (!shouldShowMentionsEditor) {
      message = { ...message, mentions: null };
    }
    onSubmit(message);
  }

  function renderHeader() {
    const title = getSocialAccountName(socialAccount) || t(`admin:${network}`);

    return (
      <Grid xs={12} container alignItems="center" justifyContent="space-between">
        <Grid>
          <Grid container spacing={1} alignItems="center">
            <Grid>
              <SocialIcon state="active" provider={network} />
            </Grid>
            <Grid>
              <Box marginLeft={0.5}>
                <Typography variant="subtitle2">{title}</Typography>
              </Box>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    );
  }

  function renderBody(values: Variations, setValues: (values: Variations) => void) {
    const message = values.messages[values.index]?.commentary || '';
    const fieldName = `messages[${values.index}].commentary`;
    const mentionsFieldName = `messages[${values.index}].mentions`;
    const blockListFieldName = `messages[${values.index}].blockList`;

    return (
      <Grid xs={12} container>
        <Grid xs>
          <Grid direction="column" container>
            <Grid xs={12}>
              {values.messages?.length > 1 && (
                <Tabs
                  value={values.index}
                  onChange={(_: any, index: number) => setValues({ ...values, index })}
                  TabIndicatorProps={{ sx: { display: 'none' } }}
                  sx={{
                    minHeight: 2,
                    padding: theme.spacing(1, 0, 1, 2),
                    backgroundColor: theme.palette.grey[100],
                  }}
                >
                  {values.messages?.map((_: ContentCommentary, index: number) => renderTab(index))}
                </Tabs>
              )}
              <MentionsEditor
                fieldName={fieldName}
                mentionsFieldName={mentionsFieldName}
                blockListFieldName={blockListFieldName}
                placeholder={t(`sharing:${network}`)}
                publicQuery={true}
                disabled={hasShared || lockCommentary}
                disableMentions={!shouldShowMentionsEditor}
                network={network}
              />
            </Grid>
            <Grid>
              <Grid container spacing={1}>
                <Grid>
                  <TextCounter length={message?.length || 0} limit={limit} />
                </Grid>
                {renderOverLimitWarning(message)}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    );
  }

  function renderTab(index: number) {
    return (
      <Tab key={index} label={t('sharing:option_count', { count: index + 1 })} sx={styles.tab} />
    );
  }

  function renderOverLimitWarning(message: string) {
    if (message?.length <= limit) {
      return null;
    }
    return (
      <Grid>
        <InputAdornment variant="filled" position="end">
          <Typography variant="overline" sx={{ color: 'error.main' }}>
            {t('sharing:commentaryTooLong', { max: limit })}
          </Typography>
        </InputAdornment>
      </Grid>
    );
  }
  function renderShareButton(values: Variations) {
    const fieldName = `messages[${values.index}].commentary`;

    return (
      <Grid container alignItems="center">
        <PublicShareButton
          network={network}
          hasShared={hasShared}
          isLoading={loading}
          fieldName={fieldName}
          sx={{ display: { sx: 'none', md: 'block', lg: 'block' } }}
        />
      </Grid>
    );
  }

  return (
    <Formik
      initialValues={variations}
      onSubmit={handleSubmit}
      validationSchema={publicSharingSchema}
    >
      {({ values, setValues }) => (
        <PaddedPaper>
          <Grid container direction="row" spacing={2} alignItems="center">
            <Grid xs>
              <Grid container direction="column" spacing={1}>
                {renderHeader()}
                {renderBody(values, setValues)}
              </Grid>
            </Grid>
            <Grid>{renderShareButton(values)}</Grid>
          </Grid>
        </PaddedPaper>
      )}
    </Formik>
  );
};

export default PublicShareForm;
